(() => {
  if (customElements.get('ode-gift-popup')) return;
  const storageKey = 'ode-gift-signup-v2';
  let dismissedThisPage = false;
  class OdeGiftPopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.editor = Boolean(window.Shopify?.designMode);
      // A sheet sits over the page without locking it, so the visitor can keep scrolling.
      this.sheet = this.dataset.style === 'sheet';
      this.querySelectorAll('[data-gift-close]').forEach((button) => button.addEventListener('click', () => this.close(), options));
      this.querySelector('[data-gift-home]')?.addEventListener('click', () => { this.returnHome = true; }, options);
      this.querySelector('[data-gift-copy]')?.addEventListener('click', (event) => this.copy(event.currentTarget), options);
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      }, options);
      this.dialog.addEventListener('close', () => {
        clearTimeout(this.closeTimer);
        this.closing = false;
        this.dialog.classList.remove('is-closing');
        if (!this.editor) {
          dismissedThisPage = true;
          const days = this.dataset.state === 'sent' ? 365 : Math.max(1, Number(this.dataset.days) || 7);
          try { localStorage.setItem(storageKey, String(Date.now() + days * 86400000)); } catch (_) { /* Browsing still works with storage disabled. */ }
        }
        // After signing up the page sits on ?customer_posted=true. Drop that (and keep
        // anything else, like ?variant=) once the code has been seen.
        if (this.dataset.state === 'sent') {
          const url = new URL(location.href);
          url.searchParams.delete('customer_posted');
          url.hash = '';
          history.replaceState(null, '', url);
        }
        // On the homepage, land back at the top of a clean page.
        if (this.returnHome) {
          this.returnHome = false;
          window.scrollTo(0, 0);
        }
      }, options);
      this.dialog.addEventListener('keydown', (event) => {
        if (this.sheet && event.key === 'Escape') this.close();
      }, options);
      // "Join the list" links point at the homepage signup. Where this page has no
      // signup section of its own, open the invitation here instead of leaving.
      document.addEventListener('click', (event) => {
        const link = event.target.closest?.('a[href$="#newsletter-signup"], [data-gift-open]');
        if (!link || document.getElementById('newsletter-signup')) return;
        event.preventDefault();
        this.show(true);
      }, options);
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.close();
      }, options);
      document.addEventListener('shopify:section:select', (event) => {
        if (String(event.detail?.sectionId) === this.dataset.sectionId) this.show(true);
      }, options);
      document.addEventListener('shopify:section:deselect', (event) => {
        if (String(event.detail?.sectionId) === this.dataset.sectionId) this.close(true);
      }, options);
      // After the form posts, the page reloads: reopen to show the code or the error.
      if (this.dataset.state === 'sent' || this.dataset.state === 'error') {
        this.show(true);
      } else if (!this.editor && !this.dismissed()) {
        const depth = Math.min(0.8, Math.max(0, Number(this.dataset.scroll) || 0) / 100);
        let timeReady = false;
        let scrollReady = depth === 0;
        const ready = () => { if (timeReady && scrollReady) this.show(); };
        if (!scrollReady) {
          const onScroll = () => {
            const seen = (window.scrollY + window.innerHeight) / Math.max(1, document.documentElement.scrollHeight);
            if (seen < depth) return;
            scrollReady = true;
            window.removeEventListener('scroll', onScroll);
            ready();
          };
          window.addEventListener('scroll', onScroll, { passive: true, signal: this.controller.signal });
        }
        this.timer = setTimeout(() => { timeReady = true; ready(); }, Math.max(0, Number(this.dataset.delay) || 0) * 1000);
      }
    }
    dismissed() {
      if (dismissedThisPage) return true;
      try { return Number(localStorage.getItem(storageKey)) > Date.now(); } catch (_) { return false; }
    }
    async show(force = false) {
      if (!this.isConnected || this.dialog.open || this.opening || (!force && this.dismissed())) return;
      const occupied = document.hidden || document.querySelector('dialog[open], [role="dialog"][aria-modal="true"]') || document.activeElement?.matches('input, textarea, select, [contenteditable="true"]');
      if (occupied) {
        if (!force || this.dataset.state !== 'ask') this.timer = setTimeout(() => this.show(force), 1000);
        return;
      }
      if (!this.isConnected || this.dialog.open || (!force && this.dismissed())) return;
      if (this.sheet) this.dialog.show();
      else this.dialog.showModal();
    }
    async copy(button) {
      const code = this.querySelector('[data-gift-code]')?.textContent.trim();
      if (!code) return;
      try {
        await navigator.clipboard.writeText(code);
        button.textContent = 'Copied';
      } catch (_) {
        const range = document.createRange();
        range.selectNodeContents(this.querySelector('[data-gift-code]'));
        getSelection().removeAllRanges();
        getSelection().addRange(range);
      }
    }
    close(immediate = false) {
      if (!this.dialog.open || this.closing) return;
      if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.dialog.close();
        return;
      }
      this.closing = true;
      this.dialog.classList.add('is-closing');
      this.closeTimer = setTimeout(() => this.dialog.close(), 180);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.closeTimer);
      this.controller?.abort();
      if (this.dialog?.open) this.dialog.close();
    }
  }
  customElements.define('ode-gift-popup', OdeGiftPopup);
})();
